<template>
	<div class="text-left overflow-y-scroll">
		<div id="add-new-location" class="p-t-xl">
			<router-link :to="{ name: 'app' }">
				<div id="add-new-location-close">
					<font-awesome-icon icon="times" />
				</div>
			</router-link>
			<div>
				<div id="new-location-name" class="relative">
					<input type="text" v-model.number="location.name" />
					<div>
						<font-awesome-icon icon="pen" />
					</div>
				</div>

				<div id="new-location-data" class="m-t-lg">
					<p v-if="location.position && location.position.length > 0">
						{{ parseFloat(location.position[0]).toFixed(3) }} ,
						{{ parseFloat(location.position[1]).toFixed(3) }}
					</p>
					<p>{{ location.full_name }}</p>
					<p v-if="location.timezone">Timezone: {{ location.timezone }}</p>
				</div>
				<div v-if="total_locations >= max_locations">
					<div class="alert">
						<div>
							<span>You have exceeded the maximun number of locations</span>
						</div>
					</div>
				</div>
				<div v-else>
					<div v-if="!creating_location">
						<div class="p-t-l font-500">
							<span>Select a PV category:</span>
						</div>

						<div class="full-width m-t">
							<div
								v-for="(pv_cat, index) in pv_categories"
								v-bind:key="'new_location_pv_cat_' + index"
								v-on:click="set_pv_category(pv_cat.name)"
								v-bind:class="{ active: location.pv_category == pv_cat.name }"
								class="new-location-pv-category"
							>
								<div :class="pv_cat.icon_class + ' icon'"></div>
								<div class="pv-category-name">{{ pv_cat.name }}</div>
							</div>
							<div class="clear"></div>
						</div>

						<div class="m-t-xl">
							<div class="left font-500" style="width:55%;">
								Peak power (MWp)
							</div>
							<div class="right" style="width:45%;">
								<input
									type="number"
									placeholder="e.g. 120"
									v-model="location.pv_data.peak_power"
								/>
							</div>
							<div class="clear"></div>
						</div>

						<div class="m-t">
							<div
								v-if="location_validator.error && location.pv_data.peak_power"
								class="alert"
							>
								<div>{{ location_validator.error.message }}</div>
							</div>

							<div
								v-if="!location_validator.error && location.pv_data.peak_power"
								class=" margin-center text-center"
								style="width:50%"
							>
								<CustomButton
									text="Save location"
									@click.native="save_new_location(location)"
								/>
								<div class="clear"></div>
							</div>
						</div>

						<div
							id="new-location-edit-details"
							class="full-width m-t text-center"
						>
							<div v-if="edit_details">
								<div
									v-for="(pv_variable, index) in pv_data_variables"
									v-bind:key="'new_location_prop_' + index"
								>
									<div
										class="new-location-property"
										v-if="
											index == 'losses' ||
												index == 'tracking' ||
												location.pv_data.tracking == 'Fixed' ||
												(location.pv_data.tracking == '1 axis' &&
													index == 'tilt')
										"
									>
										<div class="left text-right">{{ pv_variable.label }}</div>
										<div class="left text-left">
											<select
												v-if="pv_variable.select"
												v-model="location.pv_data[index]"
											>
												<option
													v-for="(option, tracking_index) in pv_variable.select"
													v-bind:key="'tracking_select_' + tracking_index"
													:selected="option == 'Fixed' ? true : false"
													v-bind:value="option"
													>{{ option }}</option
												>
											</select>
											<input
												v-else
												type="number"
												:placeholder="
													pv_variable.optimal
														? 'Optimal'
														: default_pv_data[location.pv_category][index]
												"
												v-model.number="location.pv_data[index]"
											/>
										</div>
									</div>
								</div>

								<div class="clear"></div>
							</div>

							<div
								id="new-location-advanced"
								class="cursor-pointer m-t-lg"
								v-on:click="edit_details = !edit_details"
							>
								<span>Advanced options </span>
								<font-awesome-icon
									class="m-l-xs"
									:icon="edit_details ? 'chevron-up' : 'chevron-down'"
								/>
							</div>
						</div>
					</div>
					<div v-else class="m-t margin-center text-center">
						<span>Updating forecast...</span>
						<font-awesome-icon class="m-l" icon="spinner" pulse />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ForecastService from "@/services/ForecastService";
import Geosearch from "@/services/Geosearch";
import CustomButton from "@/components/sub-components/CustomButton";
import { pv_data_variables, default_pv_data } from "@/entities/pv_data";
import LocationValidator from "@/classes/location";
import { max_locations } from "@/config";

export default {
	name: "NewLocationLeft",
	components: {
		CustomButton
	},
	data() {
		return {
			location: {
				position: [this.$route.query.lat, this.$route.query.lng],
				name: undefined,
				full_name: undefined,
				pv_data: {
					azimuth: undefined,
					tilt: undefined,
					tracking: "Fixed",
					peak_power: undefined,
					losses: undefined
				},
				timezone: undefined
			},
			edit_details: false,
			pv_data_variables: pv_data_variables,
			default_pv_data: default_pv_data,
			creating_location: false,
			pv_categories: [
				{
					name: "Rooftop PV",
					icon_class: "icon-roof-pv"
				},
				{
					name: "Ground-mounted utility scale PV",
					icon_class: "icon-ground-pv"
				},
				{
					name: "Floating PV",
					icon_class: "icon-floating-pv"
				}
			],
			max_locations: max_locations
		};
	},
	created: async function() {
		if (this.location.name == null) {
			this.format_location_data(this.$route.query.lat, this.$route.query.lng);
		}
	},
	methods: {
		save_new_location: async function(location) {
			this.creating_location = true;

			try {
				var send_location = {};
				for (var key in location) {
					send_location[key] = location[key];
				}

				//Get location forecasts
				send_location.forecasts = await ForecastService.get_location_forecasts(
					send_location
				);

				this.$store.commit("add_location", send_location);
				this.$root.$emit("location_added", location);
				this.$router.push({ name: "app" });
			} catch (e) {
				alert("An error ocurred");
			} finally {
				this.creating_location = false;
			}
		},
		format_location_data: async function(lat, lng) {
			this.location.position = [lat, lng];
			if (!this.location.hasOwnProperty("pv_category")) {
				this.set_pv_category("Rooftop PV");
			}

			this.location.name = undefined;
			this.location.full_name = undefined;
			this.location.timezone = undefined;
			this.$forceUpdate();

			this.set_name(lat, lng);
			this.set_timezone(lat, lng);
		},

		set_timezone: async function(lat, lng) {
			var timezone = await Geosearch.search_timezone(lat, lng);
			if (timezone) {
				this.location.timezone = timezone;
				this.$forceUpdate();
			}
		},

		set_name: async function(lat, lng) {
			var coordinates_data = await Geosearch.query_coordinates(lat, lng);
			this.location.name = coordinates_data.name;
			this.location.full_name = coordinates_data.full_name;
			this.$forceUpdate();
		},

		set_pv_category(category) {
			this.location.pv_category = category;
			this.$forceUpdate();
		}
	},
	computed: {
		location_validator: function() {
			var location = this.location;
			for (var key in location.pv_data) {
				location.pv_data[key] =
					location.pv_data[key] == "" ? undefined : location.pv_data[key];
			}
			return LocationValidator.validate(location);
		},
		total_locations: function() {
			return this.$store.getters.locations.length;
		}
	},
	watch: {
		async $route(data) {
			if (data.query) {
				this.format_location_data(data.query.lat, data.query.lng);
			}
		}
	}
};
</script>

<style>
#add-new-location-close {
	position: absolute;
	right: 40px;
	top: 80px;
	color: grey;
}

#add-new-location input[type="text"],
#add-new-location input[type="number"],
#add-new-location select {
	color: white;
	background-color: transparent;
	outline: 0;
	border-width: 0 0 1px;
	border-color: grey;
	width: 100%;
	height: 25px;
	line-height: 25px;
	padding: 0 0 6px;
	border-radius: 0;
	font-size: 18px;
}

#new-location-name > div {
	position: absolute;
	right: 10px;
	top: 5px;
}
#new-location-data > p {
	font-size: 16px;
	margin: 0 0 8px;
	color: #f2f2f2;
}
.new-location-pv-category {
	float: left;
	width: 32%;
	border: 2px solid transparent;
	height: 130px;
	background-size: 80% auto;
	background-repeat: no-repeat;
	background-position: center 20px;
	color: var(--primary2);
	font-size: 12px;
	position: relative;
	cursor: pointer;
	text-align: center;
}
.new-location-pv-category > .icon {
	font-size: 80px;
	padding-top: 15px;
}
.new-location-pv-category.active {
	border: 2px solid var(--active);
	border-radius: 11px;
	color: var(--active);
}
.new-location-pv-category > .pv-category-name {
	width: 100%;
	text-align: center;
	font-size: 12px;
	position: absolute;
	top: 90px;
	margin: 0 auto;
	font-weight: 600;
}
#new-location-edit-details {
	color: var(--active);
}
.new-location-property {
	margin: 15px 0;
	width: 100%;
	float: left;
}

.new-location-property > div:first-child {
	width: 40%;
}
.new-location-property > div:nth-child(2) {
	width: 40%;
	margin-left: 15px;
}
#new-location-advanced {
	border: 1px solid var(--active);
	width: 60%;
	margin: 20px auto;
	border-radius: 5px;
	padding: 7px 10px;
}
</style>
